import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { QueryService } from "./query-service";
import { reportText } from "./guided-query";
import type {
  AnalyticsCatalog,
  QueryContext as Context,
  QueryDefinition,
  QueryResult,
} from "./types";
interface Engine {
  query: (malloy: string) => Promise<QueryResult>;
  catalog: () => Promise<AnalyticsCatalog>;
  cancel: () => void;
  key: string;
}
const EngineContext = createContext<Engine | null>(null);
export function QueryProvider({
  context,
  refreshVersion = 0,
  children,
}: {
  context: Context;
  refreshVersion?: number;
  children: ReactNode;
}) {
  const contextKey = JSON.stringify(context),
    key = contextKey + ":" + refreshVersion;
  const [current, setCurrent] = useState<{
    service: QueryService;
    key: string;
  } | null>(null);
  useEffect(() => {
    const service = new QueryService(JSON.parse(contextKey));
    setCurrent({ service, key });
    return () => service.destroy();
  }, [contextKey, key]);
  const cache = useMemo(
    () => new Map<string, Promise<QueryResult>>(),
    [current],
  );
  const query = useCallback(
    (malloy: string) => {
      if (!current || current.key !== key)
        return Promise.reject(new Error("Preparing the selected data…"));
      let request = cache.get(malloy);
      if (!request) {
        request = current.service.query(malloy);
        cache.set(malloy, request);
        void request.catch(() => cache.delete(malloy));
      }
      return request;
    },
    [current, key, cache],
  );
  const catalog = useCallback(
    () =>
      current && current.key === key
        ? current.service.getCatalog()
        : Promise.reject(new Error("Preparing the selected data…")),
    [current, key],
  );
  const cancel = useCallback(() => {
    cache.clear();
    current?.service.cancel();
  }, [current, cache]);
  const value = useMemo(
    () => ({
      query,
      catalog,
      cancel,
      key: key + ":" + (current?.key === key ? "ready" : "starting"),
    }),
    [query, catalog, cancel, key, current],
  );
  return (
    <EngineContext.Provider value={value}>{children}</EngineContext.Provider>
  );
}
export function useEngine() {
  const context = useContext(EngineContext);
  if (!context) throw new Error("QueryProvider is missing.");
  return context;
}
export function useCatalog() {
  const engine = useEngine();
  const [state, setState] = useState<{
    key: string;
    data?: AnalyticsCatalog;
    error?: string;
  }>({ key: "" });
  useEffect(() => {
    let active = true;
    void engine.catalog().then(
      (data) => {
        if (active) setState({ key: engine.key, data });
      },
      (error) => {
        if (active) setState({ key: engine.key, error: error.message });
      },
    );
    return () => {
      active = false;
    };
  }, [engine.catalog, engine.key]);
  return state.key === engine.key ? state : { key: engine.key };
}
export function useQuery(malloy?: string) {
  const engine = useEngine(),
    [attempt, setAttempt] = useState(0);
  const key = engine.key + ":" + malloy + ":" + attempt;
  const [state, setState] = useState<{
    key: string;
    data?: QueryResult;
    error?: string;
    loading: boolean;
  }>({ key: "", loading: true });
  useEffect(() => {
    let active = true;
    if (!malloy) {
      setState({ key, loading: false });
      return;
    }
    setState({ key, loading: true });
    void engine.query(malloy).then(
      (data) => {
        if (active) setState({ key, data, loading: false });
      },
      (error) => {
        if (active) setState({ key, error: error.message, loading: false });
      },
    );
    return () => {
      active = false;
    };
  }, [engine.query, malloy, key]);
  return {
    ...(state.key === key ? state : { key, loading: !!malloy }),
    retry: () => setAttempt((n) => n + 1),
  };
}
export function useReport(report: QueryDefinition) {
  return useQuery(reportText(report));
}
